// frontend_MainDashboard/src/hooks/api/useMediaApi.ts

import { useCallback } from 'react';
import { useApiBase } from './useApiBase';

export interface MediaApiHook {
  fetchMediaLibrary: () => Promise<{ success: boolean; media?: any[]; message?: string }>;
  uploadMedia: (file: File, folder?: string, onProgress?: (progress: number) => void) => Promise<{ success: boolean; media?: any; message?: string }>;
  deleteMedia: (mediaId: string) => Promise<{ success: boolean; message?: string }>;
  renameMedia: (mediaId: string, newName: string) => Promise<{ success: boolean; media?: any; message?: string }>;
  moveMedia: (mediaId: string, newFolder: string) => Promise<{ success: boolean; media?: any; message?: string }>;
}

export const useMediaApi = (): MediaApiHook => {
  const { makeRequest, makeFormDataRequest } = useApiBase();

  const fetchMediaLibrary = useCallback(async (): Promise<{ success: boolean; media?: any[]; message?: string }> => {
    return makeRequest<{ success: boolean; media?: any[]; message?: string }>('/client/media', 'GET');
  }, [makeRequest]);

  const uploadMedia = useCallback((file: File, folder?: string, onProgress?: (progress: number) => void): Promise<{ success: boolean; media?: any; message?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    return makeFormDataRequest<{ success: boolean; media?: any; message?: string }>('/client/media', 'POST', formData, onProgress);
  }, [makeFormDataRequest]);

  const deleteMedia = useCallback((mediaId: string): Promise<{ success: boolean; message?: string }> => {
    return makeRequest<{ success: boolean; message?: string }>(`/client/media/${mediaId}`, 'DELETE');
  }, [makeRequest]);

  const renameMedia = useCallback((mediaId: string, newName: string): Promise<{ success: boolean; media?: any; message?: string }> => {
    return makeRequest<{ success: boolean; media?: any; message?: string }, { name: string }>(`/client/media/${mediaId}`, 'PUT', { name: newName });
  }, [makeRequest]);

  const moveMedia = useCallback((mediaId: string, newFolder: string): Promise<{ success: boolean; media?: any; message?: string }> => {
    return makeRequest<{ success: boolean; media?: any; message?: string }, { folder: string }>(`/client/media/${mediaId}/move`, 'POST', { folder: newFolder });
  }, [makeRequest]);

  return {
    fetchMediaLibrary,
    uploadMedia,
    deleteMedia,
    renameMedia,
    moveMedia,
  };
};
